import React, { useEffect, useRef, useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Streetlights } from '../../../../../../services/types/Calculator';
import { Modal } from '../../../../../../shared/components';
import StreetlightDetails from './Details/StreetlightDetails';
import { calculateStreetLights } from '../../Utils/moduleCalculates';
import { handleKeyDown } from '../../Utils/KeyboardHandlers';
import { getNumberFormatted3 } from '../../NumberFormatterSinSeparador';

import CalculatorTableWrapper from '../Shared/CalculatorTableWrapper';
import { tooltipPreventOverflowModifiers } from '../../../../../../shared/misc/utils';

interface Props {
  streetlights: Streetlights;
  onUpdateStreetlights: (updatedStreetlights: Streetlights) => void;
}

const StreetlightSection: React.FC<Props> = ({ streetlights, onUpdateStreetlights }) => {
  const { t } = useTranslation();

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [totalCarbonSum, setTotalCarbonSum] = useState<number | null>(null);
  const [isInputDisabled, setIsInputDisabled] = useState<boolean>(true);
  const [, setIsCustomFactor] = useState<boolean>(streetlights.isCustomFactor);
  const [inputValue, setInputValue] = useState<number | null>(
    streetlights.isCustomFactor
      ? streetlights.customEmissionFactor
      : streetlights.defaultFactorValue
  );

  const inputStreetRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const bulbs = Number(streetlights.numberOfBulbs);
    const wattage = Number(streetlights.wattage);
    const hours = Number(streetlights.hoursDay);

    if (bulbs && wattage && hours) {
      const emissionFactor =
        streetlights.isCustomFactor && streetlights.customEmissionFactor
          ? streetlights.customEmissionFactor
          : streetlights.defaultFactorValue;
      const result = calculateStreetLights(bulbs, wattage, hours, emissionFactor);

      onUpdateStreetlights({
        ...streetlights,
        numberOfBulbs: bulbs,
        wattage,
        hoursDay: hours,
        customEmissionFactor: streetlights.customEmissionFactor,
        moduleId: 0,
        subTotal: result,
      });
      setTotalCarbonSum(result);
    } else {
      setTotalCarbonSum(null);
      onUpdateStreetlights({
        ...streetlights,
        numberOfBulbs: streetlights.numberOfBulbs,
        wattage: streetlights.wattage,
        hoursDay: streetlights.hoursDay,
        moduleId: streetlights.moduleId,
        customEmissionFactor: streetlights.customEmissionFactor,
        subTotal: null,
      });
    }
  }, [
    streetlights.numberOfBulbs,
    streetlights.wattage,
    streetlights.hoursDay,
    streetlights.defaultFactorValue,
    streetlights.customEmissionFactor,
  ]);

  const handleInputHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    const regex = /^[0-9]*\.?[0-9]*$/;

    if (!regex.test(value)) {
      return;
    }

    if (value.length > 10) {
      return;
    }

    const numericValue = value !== '' ? Number(value) : null;

    if (numericValue !== null && numericValue > 24) {
      return;
    }

    onUpdateStreetlights({
      ...streetlights,
      hoursDay: numericValue,
    });
  };

  const handleInputBulbsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    onUpdateStreetlights({
      ...streetlights,
      numberOfBulbs: value === '' ? null : Number(value),
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    onUpdateStreetlights({
      ...streetlights,
      customEmissionFactor: value === '' ? null : Number(value),
      isCustomFactor: true,
    });
    if (streetlights.customEmissionFactor === null) {
      setTotalCarbonSum(null);
    }

    setInputValue(value === '' ? null : Number(value));

    setIsCustomFactor(true);
  };

  const handleInputWattageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    onUpdateStreetlights({
      ...streetlights,
      wattage: value === '' ? null : Number(value),
    });
  };

  const handleBlurInput = () => {
    setIsInputDisabled(true);
    if (inputValue === null) {
      setInputValue(streetlights.defaultFactorValue);
      setIsCustomFactor(false);
      onUpdateStreetlights({
        ...streetlights,
        isCustomFactor: false,
      });
    }
  };

  const setCustomFactor = () => {
    onUpdateStreetlights({ ...streetlights, isCustomFactor: true });
    setIsInputDisabled(false);
  };

  useEffect(() => {
    if (!isInputDisabled && inputStreetRef.current) {
      inputStreetRef.current.focus();
    }
  }, [isInputDisabled]);

  return (
    <>
      <div className="content-calculator">
        <div className="row calculator-title-row">
          {t('Determine the number and wattage of all bulbs in the system ')}
          <span id="tr-1324" style={{ marginLeft: '5px' }}>
            <FontAwesomeIcon icon="info-circle" className="calculator-icon" />
          </span>
          <UncontrolledTooltip
            className="calculator-instruction"
            placement="bottom"
            target="tr-1324"
            modifiers={tooltipPreventOverflowModifiers}
          >
            {t('moduleStreetLightsInfo.WattageNumber')}
          </UncontrolledTooltip>
        </div>
        <div className="row calculator-input-row">
          <div className="col-12 col-sm-5 col-md-3 col-lg-2">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('Number of Bulbs')}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-4 col-lg-2">
            <input
              title="Number of Bulbs"
              type="number"
              id="numberBulbsInput"
              value={streetlights.numberOfBulbs !== null ? streetlights.numberOfBulbs : ''}
              onChange={handleInputBulbsChange}
              min="0"
              max={99999999.99}
              maxLength={10}
              onKeyDown={handleKeyDown}
              className="calculator-input"
            />
          </div>
        </div>
        <div className="row calculator-input-row row-border">
          <div className="col-12 col-sm-5 col-md-3 col-lg-2">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('Bulb wattage')}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-4 col-lg-2">
            <input
              title="Bulb wattage"
              type="number"
              id="wattegeInput"
              value={streetlights.wattage !== null ? streetlights.wattage : ''}
              onChange={handleInputWattageChange}
              min="0"
              maxLength={10}
              onKeyDown={handleKeyDown}
              className="calculator-input"
            />
          </div>
        </div>
        <div className="row calculator-title-row">
          {t('Estimate the average annual daily operating hours for each group of lights')}
          <span id="tr-132554" style={{ marginLeft: '5px' }}>
            <FontAwesomeIcon icon="info-circle" className="calculator-icon" />
          </span>
          <UncontrolledTooltip
            className="calculator-instruction"
            placement="bottom"
            target="tr-132554"
            modifiers={tooltipPreventOverflowModifiers}
          >
            {t('moduleStreetLightsInfo.OperatingHours')}
          </UncontrolledTooltip>
        </div>
        <div className="row calculator-input-row row-border">
          <div className="col-12 col-sm-5 col-md-3 col-lg-2">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('Hours/Day')}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-4 col-lg-2">
            <input
              title="Hours/Day"
              type="number"
              id="hoursInput"
              value={streetlights.hoursDay !== null ? streetlights.hoursDay : ''}
              onChange={handleInputHoursChange}
              min="0"
              maxLength={10}
              onKeyDown={handleKeyDown}
              className="calculator-input"
            />
          </div>
        </div>
        <div className="row calculator-title-row">
          {t('Select appropriate emission factor')}
          <span id="tr-132466" style={{ marginLeft: '5px' }}>
            <FontAwesomeIcon icon="info-circle" className="calculator-icon" />
          </span>
          <UncontrolledTooltip
            className="calculator-instruction"
            placement="bottom"
            target="tr-132466"
            modifiers={tooltipPreventOverflowModifiers}
          >
            {t('moduleStreetLightsInfo.EmissionFactor')}
          </UncontrolledTooltip>
        </div>
        <div className="row calculator-input-row">
          <div className="col-12 col-sm-5 col-md-3 col-lg-2">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('CO (T/MWh)')}
              {streetlights.defaultFactorValue && (
                <div className="calculator-default-roadMaintenance">
                  (Default CO<sub>2</sub>, {getNumberFormatted3(streetlights.defaultFactorValue)})
                </div>
              )}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-4 col-lg-2">
            <input
              ref={inputStreetRef}
              type="number"
              id="emissionFactorInput"
              value={inputValue !== null ? inputValue : ''}
              min="0"
              className="calculator-input"
              disabled={isInputDisabled}
              onChange={handleInputChange}
              onBlur={handleBlurInput}
              title="CO (T/MWh)"
              onKeyDown={handleKeyDown}
            />
            <UncontrolledTooltip
              className="tooltip-toolkit"
              placement="bottom"
              target="emissionFactorInput"
              modifiers={tooltipPreventOverflowModifiers}
            >
              {t('Default factor for the country')}
            </UncontrolledTooltip>
          </div>
          {isInputDisabled && (
            <>
              <button
                title="Edit"
                type="button"
                className="gi-icon"
                onClick={setCustomFactor}
                id="editIcon_street"
              >
                <FontAwesomeIcon icon="edit" />
              </button>
              <UncontrolledTooltip
                className="tooltip-calculator"
                placement="bottom"
                target="editIcon_street"
                modifiers={tooltipPreventOverflowModifiers}
              >
                <div>
                  {t('Custom CO')}
                  <sub>2</sub>
                </div>
              </UncontrolledTooltip>
            </>
          )}
        </div>
      </div>
      <CalculatorTableWrapper
        totalCarbonSum={totalCarbonSum}
        onShowDetailsModal={() => setShowDetailsModal(true)}
        subtotalColSpan={3}
        colGroup={
          <colgroup>
            <col style={{ width: '55%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
        }
      />
      <Modal
        title={streetlights.name}
        visible={showDetailsModal}
        setVisible={() => setShowDetailsModal(false)}
        actions={[{ text: 'Close', action: () => setShowDetailsModal(false) }]}
        customClass="extra_wide-modal"
        onClosed={() => setShowDetailsModal(false)}
      >
        <div className="mb-0 table-responsive">
          <StreetlightDetails streetlights={streetlights} />
        </div>
      </Modal>
    </>
  );
};

export default StreetlightSection;
